import axios from 'axios'

import environment from '@/environment'
import type {
  AddRampCurveRequest,
  AttributeValueType,
  ManualSteeringCommand,
  RampCurvesResponse,
  Resource,
  ResourceActivationGroupsResponse,
  ResourceDetails,
  ResourceLifecycleStage,
  ResourceMetadata,
  ResourceMultistepStrategyType,
  ResourceSteeringDefaultType,
  ResourceType,
  UpdateRampCurveRequest,
} from '@/features/resource/types'
import type { MarketProgramType } from '@/types/marketProgramType'

export const GET_RESOURCES_API_ID = 'GET_RESOURCES'
export const GET_RESOURCE_API_ID = 'GET_RESOURCE'
export const GET_RESOURCES_BY_CONTROLLER_EQUIPMENT_NUMBER_API_ID = 'GET_RESOURCES_BY_CONTROLLER_EQUIPMENT_NUMBER'
export const POLL_RESOURCES_API_ID = 'POLL_RESOURCES'
export const GET_BATTERY_INFO_API_ID = 'GET_BATTERY_INFO'
export const GET_RESOURCE_RAMP_CURVES_API_ID = 'GET_RESOURCE_RAMP_CURVES'
export const GET_RESOURCE_ACTIVATION_GROUPS_API_ID = 'GET_RESOURCE_ACTIVATION_GROUPS'

export type ResourcesResponse = { data: Resource[]; meta: ResourceMetadata }
export type PollResourcesResponse = { data: Resource[] | null }

type Filters = {
  customerId?: string
  resourceType?: ResourceType
  countryCode?: string
  marketProgram?: MarketProgramType
  lifecycleStage?: ResourceLifecycleStage
  searchKey?: string
}

// Name means the name of the resource
export type SortProperty = 'resourceName' | 'customerName'

export type SortDirection = 'asc' | 'desc'

/**
 * The format of the source will be "property,direction"
 *
 * Property can be name or customerName
 * Direction can be asc or desc
 *
 * By default, the API is sorting resources by resource name ascending
 */
export type Sort = {
  property: SortProperty
  direction: SortDirection
}

type Pagination = {
  limit?: number
  skip?: number
}

export type UpdateResourcePayload = {
  resourceName: string
  resourceType: ResourceType
}

export interface AddSteeringRangeRequest {
  max: SteeringRangeEntryRequest
  min: SteeringRangeEntryRequest
  step: SteeringRangeEntryRequest
  multistepMappings: MultistepMappingRequest[] | null
}

export interface UpdateSteeringRangeRequest {
  max: SteeringRangeEntryRequest
  min: SteeringRangeEntryRequest
  step: SteeringRangeEntryRequest
  multistepMappings: MultistepMappingRequest[] | null
}

export interface SteeringRangeEntryRequest {
  valueType: 'ABSOLUTE' | 'DYNAMIC'
  value: number | null
  minSecondsOnThisLevel: number | null
  maxSecondsOnThisLevel: number | null
}

export interface MultistepMappingRequest {
  stepValue: number | null
  controlPortId: string | null
}

/**
 * Battery telemetry for one control port, as reported by the battery itself.
 * **Energy fields are Wh and power fields are W.** Consumers must scale; don't assume kWh from the
 * field names (LC-416: the operating-envelope energy axis read 1000× high for exactly that reason).
 *
 * `ratedEnergy` is a pass-through from the battery. Most other fields are not: `maxUsablePower` in
 * particular is derived per control mode upstream, so trust the per-field units below rather than
 * assuming the whole shape is raw device data.
 *
 * NULLABILITY: every field here is nullable upstream (`ratedEnergy: Int?`, `stateOfHealth: Double?`,
 * `controlPortId: UUID?` in the BFF's DTO) — a battery that hasn't reported yet sends nothing. The
 * types below deliberately do NOT say so: adding `| null` type-errors four call sites in
 * `BatterySteeringPage.tsx` and one in `SoCLiveReadout.tsx`, both out of LC-416's scope. The failure
 * that costs you is not a crash but a plausible zero: `null / 1000` is `0`, so an unreported field
 * renders a confident "0 kW", and `Math.round(null * 100)` renders "0 %". Guard every read with
 * `?? null` rather than trusting these types.
 */
export type BatteryInfoResponse = {
  controlPortId: string
  /** Ratio in [0, 1] — 0.99 means 99 % healthy, NOT 99. */
  stateOfHealth: number
  /** Ratio in [0, 1] — 0.47 means 47 % charged, NOT 47. */
  stateOfCharge: number
  /** Wh */
  energyToFullCharge: number
  /** W */
  maxUsablePower: number
  /** W */
  minUsablePower: number
  /** Wh */
  energyToFullDischarge: number
  /**
   * Wh — a 2500 kWh battery arrives as 2_500_000.
   *
   * OPEN QUESTION, do not assume either way in user-facing copy: is this the battery's NAMEPLATE
   * rating, or already derated for state of health? The sources disagree.
   *  - FOR already-derated: `sympower-controller-protocol` `ResourceReport.proto:79` says
   *    "`ratedEnergyWh` is already SOH-adjusted by the BMS". Note this sits in the doc for a
   *    DIFFERENT field, `usableEnergyWh`, as a premise of its formula — not in `ratedEnergyWh`'s own
   *    doc, which has none. Weaker evidence than it first looks.
   *  - FOR nameplate: the monolith's `ControlBoxMem.getMaxEnergy()` computes
   *    `ratedEnergy * stateOfHealthAsFraction`, which double-applies SOH unless this is nameplate.
   *    And `BatteryStateUpdated.avdl:12` defines `stateOfHealth` as a ratio against "its rated
   *    capacity", i.e. rated is the undegraded figure.
   * That avdl is not a tiebreaker in either direction, mind: it also describes this same field as
   * "max amount of POWER", which is simply wrong. Until someone settles it, say only that the battery
   * reports the value.
   */
  ratedEnergy: number
}

export interface RepublishResourceRequestAndResponse {
  config: boolean
  steering: boolean
  steerabilityAndBattery: boolean
}

export interface ValidationErrorResponse {
  status: string
  message: string
}

/**
 * Target-SoC command body (LC-292 / LC-291 Layer-1 contract). Every value here is already in the
 * downstream units — `targetSoC` a fraction 0..1, setpoints integer Watts, ramps integer
 * Watts/second, `validUntil` an ISO-8601 string; the UI→wire conversion lives in
 * `utils/targetSoC` (the sole unit converter). Field names are case-sensitive and must match the
 * BFF DTO exactly.
 */
export type TargetSoCRequest = {
  targetSoC: number
  maxSetpointCharge?: number
  maxSetpointDischarge?: number
  maxRampSpeedCharge?: number
  maxRampSpeedDischarge?: number
  validUntil: string
}

export type TargetSoCResponse = {
  commandId: string
}

const BASE_RESOURCES_API_URL = `${environment.services.resourcesManagerApiUrl}/resources`

export const parseSort = (sort: Sort): string => {
  return `${sort.property},${sort.direction}`
}

/**
 * We just send the filters that content a value. If not of them have a value, we don't send any filter at all.
 */
function parseFilters(filters: Filters): string | null {
  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      params.append(key, value)
    }
  }

  const searchParams = params.toString()

  return searchParams.length > 0 ? searchParams : null
}

export async function getResources({
  filters,
  pagination,
  sort,
}: {
  filters?: Filters
  pagination?: Pagination
  sort?: Sort
}): Promise<ResourcesResponse> {
  const parsedFilters = filters ? parseFilters(filters) : undefined

  const response = await axios.get<ResourcesResponse>(`${BASE_RESOURCES_API_URL}`, {
    params: {
      ...pagination,
      sort: sort ? parseSort(sort) : undefined,
      filters: parsedFilters ?? undefined,
    },
  })

  return response.data
}

export async function updateResource(resourceId: string, payload: UpdateResourcePayload): Promise<Resource> {
  const response = await axios.patch<Resource>(`${BASE_RESOURCES_API_URL}/${resourceId}`, payload)

  return response.data
}

// This is the etag that we will send to the API to get the resources that have been updated
let etag = ''

/**
 * The reason why this function returns an object instead of a promise is because pollResourcesBuilder will manage to
 * update the etag internally when it is necessary.
 */

export async function pollResources({
  resourceIds,
  sort,
}: {
  resourceIds: string[]
  sort?: Sort
}): Promise<PollResourcesResponse | null> {
  const params = new URLSearchParams()

  for (const id of resourceIds) {
    params.append('resourceIds', id)
  }

  if (sort) {
    params.append('sort', parseSort(sort))
  }

  const response = await axios.get<PollResourcesResponse | string>(`${BASE_RESOURCES_API_URL}/poll`, {
    params,
    headers: {
      'If-None-Match': etag,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
    },
  })

  if (response.status === 200 && typeof response.data !== 'string') {
    etag = response.headers.etag

    return { ...response.data }
  }

  return null
}
export async function getResource(resourceId: string): Promise<ResourceDetails> {
  const response = await axios.get(`${BASE_RESOURCES_API_URL}/${resourceId}`)
  return response.data
}

export async function getResourcesByControllerEquipmentNumber(controllerEquipmentNumber: number): Promise<Resource[]> {
  const response = await axios.get<Resource[]>(
    `${BASE_RESOURCES_API_URL}/by-controller-equipment-number/${controllerEquipmentNumber}`,
  )
  return response.data
}

export const updateSteeringDefaults = async (
  resourceId: string,
  steeringDefaultType: ResourceSteeringDefaultType,
  defaultConsumptionLevel: number | null,
) => {
  const response = await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/steering-defaults`, {
    steeringDefaultType,
    defaultConsumptionLevel,
  })
  return response.data
}

export async function addSteeringRange(resourceId: string, payload: AddSteeringRangeRequest): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/steering-ranges`, payload)
}

export async function updateSteeringRange(
  resourceId: string,
  rangeId: string,
  payload: UpdateSteeringRangeRequest,
): Promise<void> {
  await axios.put(`${BASE_RESOURCES_API_URL}/${resourceId}/steering-ranges/${rangeId}`, payload)
}

export async function deleteSteeringRange(resourceId: string, rangeId: string): Promise<void> {
  await axios.delete(`${BASE_RESOURCES_API_URL}/${resourceId}/steering-ranges/${rangeId}`)
}

export async function getBatteryInfo(controlPortId: string): Promise<BatteryInfoResponse> {
  const response = await axios.get<BatteryInfoResponse>(
    `${BASE_RESOURCES_API_URL}/batteries/${controlPortId}/battery-info`,
  )
  return response.data
}

export async function updateBatterySoC(resourceId: string, desiredSoC: number): Promise<void> {
  await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/battery-soc`, { desiredSoC })
}

export async function setTargetSoC(resourceId: string, body: TargetSoCRequest): Promise<TargetSoCResponse> {
  const response = await axios.post<TargetSoCResponse>(`${BASE_RESOURCES_API_URL}/${resourceId}/target-soc`, body)
  return response.data
}

// Cancel is a POST to keep the target-SoC API consistently POST end-to-end (Variant A). It carries
// NO request body (Layer-1 contract); the BFF mints the commandId server-side.
export async function cancelTargetSoC(resourceId: string): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/target-soc/cancellation`)
}

export async function steerResource(resourceId: string, manualSteeringCommand: ManualSteeringCommand): Promise<void> {
  await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/manual-steering`, manualSteeringCommand)
}

export const updateSteeringCapabilities = async (
  resourceId: string,
  isReleaseControlSupported: boolean,
  minReleaseControlTimeInSeconds: number | null,
) => {
  const response = await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/steering-capabilities`, {
    isReleaseControlSupported,
    minReleaseControlTimeInSeconds,
  })
  return response.data
}

export const updateSteeringMultistep = async (
  resourceId: string,
  isMultistepResource: boolean,
  multistepStrategy: ResourceMultistepStrategyType | null,
): Promise<void> => {
  const response = await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/multistep-settings`, {
    isMultistepResource,
    multistepStrategy,
  })
  return response.data
}

export const updateBatteryUsableEnergy = async (resourceId: string, userDefinedUsableEnergy: number | null) => {
  const response = await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/battery-usable-power`, {
    userDefinedUsableEnergy,
  })
  return response.data
}

export async function updateMeteringPortRelation(
  resourceId: string,
  meteringPortId: string,
  arithmeticOperation: string,
): Promise<void> {
  await axios.put(`${BASE_RESOURCES_API_URL}/${resourceId}/metering-ports/${meteringPortId}/relation`, {
    arithmeticOperation,
  })
}

export async function updateControlPortMeasure(
  resourceId: string,
  controlPortId: string,
  measure: string,
): Promise<void> {
  await axios.patch(`${BASE_RESOURCES_API_URL}/${resourceId}/control-ports/${controlPortId}/measure`, {
    measure,
  })
}

// support toolbox actions
export async function decommissionResource(resourceId: string): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/decommission`)
}

export async function republishResource(
  resourceId: string,
  request: RepublishResourceRequestAndResponse,
): Promise<RepublishResourceRequestAndResponse> {
  const response = await axios.post<RepublishResourceRequestAndResponse>(
    `${BASE_RESOURCES_API_URL}/${resourceId}/republish`,
    request,
  )
  return response.data
}

export async function resyncResource(resourceId: string): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/resync`)
}

export async function syncResourceComponentStatusesFromMonolith(resourceId: string): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/sync-component-statuses`)
}

export const updateResourceCustomAttributes = async (
  resourceId: string,
  namespace: string,
  customAttributeValues: Record<string, AttributeValueType>,
): Promise<void> => {
  const response = await axios.put(
    `${BASE_RESOURCES_API_URL}/${resourceId}/custom-attributes/${namespace}`,
    customAttributeValues,
  )
  return response.data
}

export async function addRampCurve(resourceId: string, payload: AddRampCurveRequest): Promise<void> {
  await axios.post(`${BASE_RESOURCES_API_URL}/${resourceId}/ramp-curves`, payload)
}

export async function updateRampCurve(
  resourceId: string,
  rampCurveId: string,
  payload: UpdateRampCurveRequest,
): Promise<void> {
  await axios.put(`${BASE_RESOURCES_API_URL}/${resourceId}/ramp-curves/${rampCurveId}`, payload)
}

export async function deleteRampCurve(resourceId: string, rampCurveId: string): Promise<void> {
  await axios.delete(`${BASE_RESOURCES_API_URL}/${resourceId}/ramp-curves/${rampCurveId}`)
}

export async function getRampCurves(resourceId: string): Promise<RampCurvesResponse> {
  const response = await axios.get<RampCurvesResponse>(`${BASE_RESOURCES_API_URL}/${resourceId}/ramp-curves`)
  return response.data
}

export const bulkImportRampCurves = async (file: File): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file)

  await axios.post(`${BASE_RESOURCES_API_URL}/ramp-curves/bulk-import`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export async function getResourceActivationGroups(resourceUuid: string): Promise<ResourceActivationGroupsResponse> {
  const response = await axios.get<ResourceActivationGroupsResponse>(
    `${environment.services.portfolioManagerApiUrl}/resource/${resourceUuid}/activation-groups`,
  )
  return response.data
}
